import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, type ReactNode } from 'react';

import { useDocumentTitle } from '@/lib/useDocumentTitle';

interface StatusPageProps {
  /** The heading, which is also what a screen reader announces on arrival. */
  title: string;
  /** The explanation beneath it. */
  children: ReactNode;
  /** The address concerned, shown as code at the start of the explanation. */
  address?: string;
  /** The one way on. */
  action: ReactNode;
  /** The browser tab's title. */
  documentTitle: string;
  /**
   * The page's area for the testing team's tests: the page is
   * `<testId>-page`, and its title, address and message get `-title`,
   * `-address` and `-message`. See docs/test-ids.md.
   */
  testId: string;
}

/**
 * One shape for every page that says something went wrong with getting here:
 * no access, no such page, or a page that failed. A heading, an explanation,
 * the address where there is one, and one way back.
 */
export function StatusPage({
  title,
  children,
  address,
  action,
  documentTitle,
  testId,
}: StatusPageProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  useDocumentTitle(documentTitle);

  /*
   * Move focus to the heading on arrival.
   *
   * Nothing reloads on a client-side route change, so a screen reader is told
   * nothing: somebody activated a link and heard silence, with no idea where
   * they had landed. Focusing the heading announces it and puts the keyboard
   * at the top of the new page rather than wherever the old one left it.
   */
  useEffect(() => {
    heading.current?.focus();
  }, []);

  return (
    <Box data-testid={`${testId}-page`}>
      <Typography
        ref={heading}
        variant="h5"
        component="h1"
        // -1 makes it focusable programmatically without adding a tab stop
        // people have to pass through on every visit.
        tabIndex={-1}
        data-testid={`${testId}-title`}
        sx={{ fontWeight: 600, '&:focus': { outline: 'none' } }}
      >
        {title}
      </Typography>

      <Typography sx={{ mt: 1 }} color="text.secondary" data-testid={`${testId}-message`}>
        {address ? (
          <>
            <Box
              component="code"
              data-testid={`${testId}-address`}
              sx={{ bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}
            >
              {address}
            </Box>{' '}
          </>
        ) : null}
        {children}
      </Typography>

      <Box sx={{ mt: 3 }}>{action}</Box>
    </Box>
  );
}
